import { useTitle } from "@/hooks/use-title.hook.ts";
import { WorkflowEditSidebar } from "@/pages/workflows/edit/components/WorkflowEditSidebar.tsx";
import { WorkflowTriggerScheduleRule } from "@/pages/workflows/edit/components/WorkflowTriggerScheduleRule.tsx";
import { WorkflowTriggerRecurringRule } from "@/pages/workflows/edit/components/WorkflowTriggerRecurringRule.tsx";
import {
  getApiWorkflowScheduledEntityType,
  useWorkflowEditPage,
  WorkflowEditPageProps,
  WorkflowEditPageStep,
} from "@/pages/workflows/edit/pages/workflow-edit-pages.tsx";
import { WorkflowTitleSlot } from "@/pages/workflows/edit/pages/WorkflowTitleSlot.tsx";
import analyticsService, {
  analyticEvents,
  analyticProperties,
} from "@/services/analytics-service.ts";
import {
  ApiWorkflowTriggerScheduleEntityType,
  ApiWorkflowTriggerType,
} from "@/types/workflows/workflow.base-enum.types.ts";
import { WorkflowTriggerRecurringRuleRef } from "@/types/workflows/workflow.types.ts";
import { Button, FullScreenPage, ListItem, Skeleton, Stack, Title } from "@skailer-com/uikit";
import { useRef, useState } from "react";
import { useTranslation } from "react-i18next";

export const WorkflowEditAutomationConfigurationPage = ({
  workflow,
  workflowVersion,
}: WorkflowEditPageProps) => {
  const { t } = useTranslation();
  const { confirmExit, navigateToStep } = useWorkflowEditPage();

  const [isLoading, setIsLoading] = useState(false);
  const componentRef = useRef<WorkflowTriggerRecurringRuleRef>(null);

  useTitle(t("workflow:configure_automation_trigger"), t("workflow:workflows"));

  const handleContinue = async () => {
    setIsLoading(true);
    const result = await componentRef.current?.saveData();
    setIsLoading(false);
    if (result) {
      analyticsService.trackEvent(analyticEvents.workflows.edited, {
        [analyticProperties.id]: workflow.id,
        [analyticProperties.tab]: WorkflowEditPageStep.automation_setup,
      });

      navigateToStep(workflow.id, WorkflowEditPageStep.flow);
    }
  };

  const renderController = () => {
    if (workflow.trigger_type === ApiWorkflowTriggerType.recurring) {
      return (
        <WorkflowTriggerRecurringRule
          ref={componentRef}
          workflow={workflow}
          rule={workflowVersion.trigger_recurring_rule}
        />
      );
    }

    const rule = workflowVersion.trigger_schedule_rule;
    if (!rule) return null;

    if (
      rule.entity_type == ApiWorkflowTriggerScheduleEntityType.time_off ||
      rule.entity_type == ApiWorkflowTriggerScheduleEntityType.employee ||
      rule.entity_type == ApiWorkflowTriggerScheduleEntityType.custom_table_row
    )
      return (
        <WorkflowTriggerScheduleRule
          ref={componentRef}
          workflow={workflow}
          workflowVersion={workflowVersion}
          workflowTriggerScheduleRule={rule}
        />
      );

    return null;
  };

  return (
    <FullScreenPage
      isCenterVertical={false}
      titleSlot={
        <WorkflowTitleSlot
          step={WorkflowEditPageStep.automation_setup}
          workflow={workflow}
          workflowVersion={workflowVersion}
        />
      }
      backSlot={
        <Button variant={"secondary"} size={"lg"} onClick={() => confirmExit()}>
          {t("common:exit")}
        </Button>
      }
      actionsSlot={
        <Stack direction={"horizontal"} gap={"sm"}>
          <Button
            onClick={() => {
              navigateToStep(workflow.id, WorkflowEditPageStep.automation);
            }}
            variant="secondary"
            size={"lg"}
          >
            {t("common:back")}
          </Button>
          <Button size={"lg"} onClick={handleContinue}>
            {t("common:continue")}
          </Button>
        </Stack>
      }
      sidebarSlot={
        <WorkflowEditSidebar
          step={WorkflowEditPageStep.automation_setup}
          workflow={workflow}
          workflowVersion={workflowVersion}
        />
      }
    >
      <Stack gap={"sm"} justify={"center"}>
        <Stack className={"card-panel"} gap={"lg"}>
          {workflow.trigger_type === ApiWorkflowTriggerType.recurring ? (
            <Title size="lg" header={t("workflow:trigger_recurring_title")} />
          ) : workflowVersion.trigger_schedule_rule ? (
            <Title
              size={"lg"}
              header={
                getApiWorkflowScheduledEntityType(
                  workflowVersion.trigger_schedule_rule.entity_type,
                  workflowVersion.trigger_schedule_rule.entity_field
                )?.title ?? t("workflow:configure_automation_trigger")
              }
            />
          ) : (
            <Skeleton className={"w-full h-10"} />
          )}

          {renderController()}

          {workflow.trigger_type === ApiWorkflowTriggerType.scheduled && (
            <ListItem
              emoji={":information_source:"}
              caption={t("workflow:trigger_schedule_catch_up_hint")}
              behavior={"card"}
            />
          )}
        </Stack>
        <Button type={"submit"} size={"lg"} isLoading={isLoading} onClick={handleContinue}>
          {t("common:continue")}
        </Button>
      </Stack>
    </FullScreenPage>
  );
};
